import React from 'react'
import { useHistory } from 'react-router-dom'
import {
  Button,
  Chip,
  Typography,
  Breadcrumbs,
  Link
} from '@material-ui/core'
import { ArrowBack, NavigateNext } from '@material-ui/icons'

/**
 * Shared header bar for every page inside the Bid Estimation module.
 *
 * Props:
 * - backTo:      string | null           — path for the "Back" button. If null, no back button.
 * - backLabel:   string                  — label for the back button (default "Back")
 * - breadcrumbs: [{ label, to? }, ...]  — breadcrumb trail; the last one is the current page
 *                                         and is rendered as plain text (no link). Earlier ones
 *                                         with a `to` are rendered as clickable links.
 * - title:       string                  — big title shown below the breadcrumbs
 * - statusChip:  { label, style }        — optional status chip next to the title
 * - actions:     ReactNode               — right-side action buttons (e.g. "Import", "Export")
 *
 * Styling mirrors the dark header bar used on ProjectDashboard.
 */
const BidEstimationHeader = ({
  backTo = null,
  backLabel = 'Back',
  breadcrumbs = [],
  title,
  statusChip = null,
  actions = null
}) => {
  const history = useHistory()

  const handleBack = () => {
    if (backTo) {
      history.push(backTo)
    } else {
      history.goBack()
    }
  }

  const headerStyle = {
    background: 'linear-gradient(135deg, #263238 0%, #37474f 100%)',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: 4,
    marginBottom: 16,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  }

  const topRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: breadcrumbs.length > 0 ? 8 : 0
  }

  const leftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap'
  }

  const titleStyle = {
    fontWeight: 600,
    color: '#fff',
    margin: 0
  }

  return (
    <div style={headerStyle}>
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" style={{ color: 'rgba(255,255,255,0.5)' }} />}
          style={{ marginBottom: 4 }}
        >
          {breadcrumbs.map((crumb, idx) => {
            const isLast = idx === breadcrumbs.length - 1
            if (isLast || !crumb.to) {
              return (
                <Typography
                  key={idx}
                  variant="caption"
                  style={{ color: isLast ? '#fff' : 'rgba(255,255,255,0.7)' }}
                >
                  {crumb.label}
                </Typography>
              )
            }
            return (
              <Link
                key={idx}
                component="button"
                variant="caption"
                onClick={() => history.push(crumb.to)}
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '0.75rem'
                }}
              >
                {crumb.label}
              </Link>
            )
          })}
        </Breadcrumbs>
      )}

      <div style={topRowStyle}>
        <div style={leftStyle}>
          {backTo !== null && (
            <Button
              size="small"
              startIcon={<ArrowBack />}
              onClick={handleBack}
              style={{ color: '#fff', textTransform: 'none' }}
            >
              {backLabel}
            </Button>
          )}
          <Typography variant="h6" style={titleStyle}>
            {title}
          </Typography>
          {statusChip && (
            <Chip
              size="small"
              label={statusChip.label}
              style={statusChip.style || { backgroundColor: '#455a64', color: '#fff' }}
            />
          )}
        </div>
        {actions && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}

export default BidEstimationHeader
